import { Group, Skeleton } from '@mantine/core'
import { ReactQuery } from 'cms-api'
import Image from 'next/image'
import Link from 'next/link'
import React, { useState } from 'react'
import { formatPrice } from 'shared-constants'
import styled from 'styled-components'
import { AddToFavorites } from 'components/AddToFavorites'
import { Box, Flex } from 'components/Layout'
import { OptionalLink } from 'components/Link'
import { Text } from 'components/Typography'
import { ASPECT_RATIO } from 'components/UploadImage'
import { useUserAuth } from 'providers/userAuth'
import {
  BOX_SHADOW,
  COLOR_INTENT,
  FONT_SIZE,
  FONT_STACK,
  FONT_WEIGHT,
  RADIUS,
  LINE_HEIGHT,
  SPACE,
} from 'Theme'
import { useMedia } from 'useMedia'
import { getProductRoute } from 'utils/getProductRoute'
import { getProductSlug, getVariantSlug } from 'utils/getProductSlug'
import { BlackFridayBadge } from './BlackFridayBadge'
import { SouldOutBadge } from './SoldOutBadge'

const CAROUSEL_IMAGE_WIDTH = SPACE.PX_22

const CarouselImage = styled(Image)`
  border-radius: ${RADIUS.CIRCLE};
  width: ${CAROUSEL_IMAGE_WIDTH};
  aspect-ratio: 1/1;
`

export type ProductThumbnailCardProps = {
  productId: number
  variantId: number
  productImageUrl: string
  productImageAlt: string
  title: string
  price: number
  tradePrice: number
  priceRange?: {
    min: number
    max: number
  }
  tradePriceRange?: {
    min: number
    max: number
  }
  tradeDiscount?: number
  url: string
  options?: string[]
  onRefetch?: () => void
  carouselImages?: {
    legacyVariantId: number
    imageUrl: string
    imageAlt: string
  }[]
  breakoutOptionCount: number
  availabilityOrder?: number
  brand?: string
  shouldDisplayBlackFridayBadge: boolean
}

export const ProductThumbnailCard = ({
  productId,
  variantId,
  productImageUrl,
  productImageAlt,
  title,
  price,
  tradePrice,
  priceRange,
  tradePriceRange,
  tradeDiscount,
  url,
  options,
  onRefetch,
  carouselImages,
  breakoutOptionCount,
  availabilityOrder,
  brand,
  shouldDisplayBlackFridayBadge,
}: ProductThumbnailCardProps) => {
  const { user, isClientSignedIn, isExpertSignedIn } = useUserAuth()
  const { MOBILE } = useMedia()
  const [isMainImageLoaded, seIsMainImageLoaded] = useState(false)

  const {
    data: favoriteUserProductVariant,
    refetch: refetchFavoriteUserProductVariant,
  } = ReactQuery.useGetFavoriteUserProductVariantBookmarkLegacyProductVariantId(
    variantId?.toString(),
    {
      query: {
        enabled:
          (isClientSignedIn || isExpertSignedIn) &&
          typeof variantId === 'number',
      },
    }
  )

  const shouldDisplayTradePrice =
    user?.isTradeCapable && tradePrice && tradePrice !== price

  const isBookmarked = !!favoriteUserProductVariant?.data?.isBookmarked

  const shouldDisplayPriceRange =
    priceRange &&
    priceRange.min > 0 &&
    priceRange.max > 0 &&
    priceRange.min !== priceRange.max

  const shouldDisplayTradePriceRange =
    tradePriceRange &&
    tradePriceRange.min > 0 &&
    tradePriceRange.max > 0 &&
    tradePriceRange.min !== tradePriceRange.max

  const formattedPrice = () => {
    if (shouldDisplayPriceRange) {
      return `${formatPrice({ price: priceRange.min })} - ${formatPrice({ price: priceRange.max })}`
    }
    return price > 0 ? formatPrice({ price }) : 'Price upon request'
  }

  const formattedTradePrice = () => {
    if (shouldDisplayTradePriceRange) {
      return `${formatPrice({ price: tradePriceRange.min })} - ${formatPrice({ price: tradePriceRange.max })}`
    }
    return tradePrice > 0
      ? formatPrice({ price: tradePrice })
      : 'Price upon request'
  }

  return (
    <Box height="100%" position="relative">
      <Flex
        style={{
          border: '1px solid white',
          overflow: 'hidden',
          boxShadow: `${BOX_SHADOW.ELEVATION_COLLECTION_CARD_1},
            ${BOX_SHADOW.ELEVATION_5}`,
          borderRadius: 10,
        }}
        height="100%"
        flexDirection="column"
      >
        <Link href={url} passHref aria-label={`Detail of ${title} product`}>
          {!isMainImageLoaded && (
            <Skeleton
              style={{ aspectRatio: ASPECT_RATIO.PRODUCTS }}
              width="100%"
              radius={10}
              animate
            />
          )}
          <Image
            alt={productImageAlt || title}
            style={{
              aspectRatio: ASPECT_RATIO.PRODUCTS,
              objectFit: 'contain',
              backgroundColor: '#fff',
              width: '100%',
              opacity: isMainImageLoaded ? 1 : 0,
              height: isMainImageLoaded ? 'auto' : 0,
              transition: 'opacity 0.2s ease-in-out',
            }}
            src={productImageUrl}
            sizes="100vw"
            width="0"
            height="0"
            onLoadingComplete={() => seIsMainImageLoaded(true)}
          />
          <Flex
            pt={SPACE.PX_16}
            pb={SPACE.PX_10}
            px={{ MOBILE: SPACE.PX_12, TABLET: SPACE.PX_16 }}
            flexDirection="column"
            justifyContent="space-between"
            flex={1}
          >
            <Box>
              <Flex gap={SPACE.PX_12} justifyContent="space-between">
                <Text
                  mb={0}
                  color={COLOR_INTENT.COLLECTION_WIDGET_CARD.TITLE}
                  fontSize={{
                    MOBILE: FONT_SIZE.PX_14,
                    TABLET: FONT_SIZE.PX_16,
                  }}
                  lineHeight={LINE_HEIGHT.L}
                  fontWeight={FONT_WEIGHT.MEDIUM}
                >
                  {title}
                </Text>
                <AddToFavorites
                  productTitle={title}
                  variantId={variantId}
                  isBookmarked={isBookmarked}
                  onRefetch={() => {
                    refetchFavoriteUserProductVariant()
                    onRefetch?.()
                  }}
                />
              </Flex>
              {brand && !brand.startsWith('NOBRAND') && (
                <Text
                  mt={SPACE.PX_4}
                  fontSize={{
                    MOBILE: FONT_SIZE.PX_11,
                    TABLET: FONT_SIZE.PX_12,
                  }}
                  color={COLOR_INTENT.SHOWROOM_CARD.BRAND}
                >
                  {brand}{' '}
                </Text>
              )}
              {options &&
                options.length > 0 &&
                options.map((option) => (
                  <Text
                    key={option}
                    fontSize={FONT_SIZE.PX_12}
                    color={COLOR_INTENT.COLLECTION_WIDGET_CARD.TITLE}
                  >
                    {option}
                  </Text>
                ))}
              <Flex
                mt={SPACE.PX_12}
                flexDirection="column"
                justifyContent="space-between"
              >
                {shouldDisplayTradePrice && (
                  <>
                    <Text
                      color={COLOR_INTENT.COLLECTION_WIDGET_CARD.TRADE_PRICE}
                      mr={SPACE.PX_12}
                      mb={SPACE.PX_4}
                      fontSize={{
                        MOBILE: FONT_SIZE.PX_13,
                        TABLET: FONT_SIZE.PX_14,
                      }}
                      fw={FONT_WEIGHT.MEDIUM}
                    >
                      Trade: {formattedTradePrice()}{' '}
                    </Text>
                    {shouldDisplayTradePrice && tradeDiscount > 0 && (
                      <Text
                        fontSize={{
                          MOBILE: FONT_SIZE.PX_13,
                          TABLET: FONT_SIZE.PX_14,
                        }}
                        fontStyle="italic"
                        mb={SPACE.PX_4}
                        fontWeight={FONT_WEIGHT.NORMAL}
                        color={COLOR_INTENT.COLLECTION_WIDGET_CARD.TRADE_PRICE}
                      >{`${tradeDiscount}% off`}</Text>
                    )}
                  </>
                )}
                <Text
                  fontSize={
                    shouldDisplayTradePrice
                      ? FONT_SIZE.PX_12
                      : {
                          MOBILE: FONT_SIZE.PX_13,
                          TABLET: FONT_SIZE.PX_14,
                        }
                  }
                  fontFamily={FONT_STACK.SANS}
                  color={COLOR_INTENT.COLLECTION_WIDGET_CARD.PRICE}
                  // isSoldOut={availability === PRODUCT_AVAILABILITY.SOLD_OUT}
                  // hasLeadTime={hasLeadTime}
                  style={{
                    textDecoration: shouldDisplayTradePrice
                      ? 'line-through'
                      : 'none',
                  }}
                >
                  {formattedPrice()}
                </Text>
              </Flex>
            </Box>
          </Flex>
        </Link>
        {carouselImages && (
          <Flex
            alignItems="flex-end"
            gap={MOBILE ? SPACE.PX_4 : SPACE.PX_8}
            flex={1}
            mb={SPACE.PX_4}
            pb={SPACE.PX_10}
            px={{ MOBILE: SPACE.PX_12, TABLET: SPACE.PX_16 }}
          >
            <Flex alignItems="center" height={SPACE.PX_26}>
              {carouselImages.length > 1 && (
                <Flex
                  gap={MOBILE ? SPACE.PX_2 : SPACE.PX_4}
                  alignItems="center"
                  mr={SPACE.PX_4}
                >
                  {carouselImages.map(
                    ({ legacyVariantId, imageUrl, imageAlt }) => (
                      <OptionalLink
                        key={legacyVariantId}
                        href={getProductRoute({
                          slug: getProductSlug({
                            id: productId,
                            title,
                          }),
                          variantSlug: getVariantSlug({
                            id: legacyVariantId,
                            title,
                          }),
                        })}
                        aria-label={imageAlt ?? ''}
                        // height={CAROUSEL_IMAGE_WIDTH}
                      >
                        <CarouselImage
                          width={22}
                          height={22}
                          src={imageUrl}
                          alt={imageAlt}
                        />
                      </OptionalLink>
                    )
                  )}
                </Flex>
              )}

              {breakoutOptionCount > Math.max(1, carouselImages.length) && (
                <Text
                  color={COLOR_INTENT.COLLECTION_WIDGET_CARD.MORE_OPTIONS}
                  fontSize={MOBILE ? FONT_SIZE.PX_9 : FONT_SIZE.PX_10}
                  pt={SPACE.PX_2}
                  fontStyle="italic"
                >
                  + More options
                </Text>
              )}
            </Flex>
          </Flex>
        )}
      </Flex>

      <Box position="absolute" top={SPACE.PX_8} left={SPACE.PX_8}>
        <Group gap="sm">
          {availabilityOrder === 1 && <SouldOutBadge />}
          {shouldDisplayBlackFridayBadge && <BlackFridayBadge />}
        </Group>
      </Box>
    </Box>
  )
}
